import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';
import { Image } from 'shared/components';

import { TruncateText } from 'src/payments/components';
import {
  BankInfo,
  BankText,
  BankName,
  DotSeparator,
} from 'src/payments/instore-cart/components/payment-information/payment-information.styles';
import { ExpandText } from 'shared/components/checkout-accordion';
import { CircleBankLogo } from 'src/payments/components/circle-bank-logo';
import { getBankDetails } from 'src/payments/utils/bank-utils';
import { PAYMENT_OPTION_ICONS } from 'src/payments/constants';
import { useCartPayment } from 'src/checkout/components/sections/payment/shared/use-cart-payment';
import { usePaymentSelectionContext } from 'src/checkout/components/sections/payment/shared/context';
import { PaymentHeader } from './header';

const EXPAND_TEST_ID = 'expand-text-payment-delivery';

export const PAYMENT_SUMMARY_TEST_ID = 'payment-summary';

export const PaymentSummary = observer(
  ({ includeContainer = false }: { includeContainer?: boolean }): JSX.Element => {
    const { paymentCardType, setExpanded } = usePaymentSelectionContext();
    const {
      onlinePaymentOption,
      bankAccounts,
      whiteLabelConfig,
      showBankInformation,
      showCreditCardInformation,
    } = useCartPayment();
    const { value: paymentValue, sublabel: paymentFee = '', label: paymentLabel = 'Credit card' } =
      onlinePaymentOption ?? {};
    const bankInformation = bankAccounts.length > 0 ? getBankDetails(bankAccounts[0]) : null;
    const { bankLogo, lastDigits, bankName = '' } = bankInformation ?? {};

    return (
      <SummaryContainer data-testid={PAYMENT_SUMMARY_TEST_ID} includeContainer={includeContainer}>
        <HeaderContainer>
          <PaymentHeader
            paymentValue={paymentValue}
            paymentLabel={paymentLabel}
            paymentFee={paymentFee}
            whiteLabelConfig={whiteLabelConfig}
          />
          <ExpandText
            data-cy={EXPAND_TEST_ID}
            data-test={EXPAND_TEST_ID}
            data-testid={EXPAND_TEST_ID}
            onClick={() => setExpanded(true, 'manual')}
          >
            Edit
          </ExpandText>
        </HeaderContainer>
        {showBankInformation && (
          <BankInfo>
            <CircleBankLogo
              altText={`${bankName} Logo`}
              logo={bankLogo}
              circleDiameter={20}
              iconHeight={14}
              iconWidth={14}
            />
            <BankText>
              <TruncateText maxWidth={125}>
                <BankName>{bankName}</BankName>
              </TruncateText>
              <DotSeparator>‧‧‧‧</DotSeparator>
              {lastDigits}
            </BankText>
          </BankInfo>
        )}
        {showCreditCardInformation && (
          <BankInfo>
            <Image alt={`${String(paymentLabel)} payment icon`} src={PAYMENT_OPTION_ICONS.creditCard} flexShrink={0} />
            {!!paymentCardType && (
              <BankText>
                <TruncateText maxWidth={125}>
                  <BankName>{paymentCardType}</BankName>
                </TruncateText>
              </BankText>
            )}
          </BankInfo>
        )}
      </SummaryContainer>
    );
  }
);

const SummaryContainer = styled.div<{ includeContainer?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  ${({ includeContainer, theme: { spaces, customized, colors } }) =>
    includeContainer &&
    css`
      padding: ${spaces[4]};
      border-radius: ${customized.radius.md};
      border: 1px solid ${colors.grey[85]};
      background: ${colors.white};
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.07);
      max-width: 390px;
    `}
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.spaces[5]};
`;
